// DATA-77 frontend follow-up — Monthly strategic-improvement review archive.
// Lists from /api/ai/strategic-reviews; selecting a row fetches /api/ai/strategic-reviews/:ym.

import { useEffect, useState } from "react";
import { useRealtimeCursor } from "../hooks/useRealtimeCursor";
import PageHeader from "../components/shared/PageHeader";
import Banner from "../components/shared/Banner";

interface ListItem {
  id: number;
  year_month: string;
  generated_at: string;
  status: string;
  model_used: string | null;
  prompt_version: string | null;
}

interface MetricRow {
  key: string;
  current: number | string | null;
  lastMonth: number | string | null;
  target: string | null;
}

interface InterventionAgg {
  intervention_type: string;
  count: number;
  outcome_30d_active: number;
  outcome_30d_separated: number;
}

interface FullReview extends ListItem {
  narrative_html: string | null;
  narrative_md: string | null;
  metrics: MetricRow[] | null;
  interventions: InterventionAgg[] | null;
  tokens_in: number | null;
  tokens_out: number | null;
  latency_ms: number | null;
  error_message: string | null;
}

export default function StrategicReviews() {
  const [items, setItems] = useState<ListItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [full, setFull] = useState<FullReview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { updatedAt } = useRealtimeCursor("strategic_review");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/ai/strategic-reviews", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`reviews → ${r.status}`);
        return (await r.json()) as { reviews: ListItem[] };
      })
      .then((d) => {
        if (cancelled) return;
        setItems(d.reviews ?? []);
        if (d.reviews?.[0] && !selected) setSelected(d.reviews[0].year_month);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [updatedAt, selected]);

  useEffect(() => {
    if (!selected) {
      setFull(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/ai/strategic-reviews/${selected}`, {
      credentials: "same-origin",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`review/${selected} → ${r.status}`);
        return (await r.json()) as { review: FullReview };
      })
      .then((d) => {
        if (!cancelled) setFull(d.review);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="Strategic Reviews"
          subtitle="Monthly Ember-authored memo: what's working, what isn't, what to try next. Generated on the 1st of each month."
        />
      </div>

      {err && (
        <Banner variant="error" className="mb-4">
          {err}
        </Banner>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        <aside>
          <p className="text-xs font-medium uppercase tracking-wider text-theme-muted mb-2">
            Months
          </p>
          <ul className="space-y-1">
            {items.length === 0 && !err && (
              <li className="text-sm text-theme-muted">
                No reviews yet — first run on the 1st.
              </li>
            )}
            {items.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setSelected(r.year_month)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                    selected === r.year_month
                      ? "bg-theme-overlay border-theme-default text-theme-primary"
                      : "border-theme-subtle text-theme-secondary hover:bg-theme-overlay/40"
                  }`}
                >
                  <div className="font-mono text-xs">{r.year_month}</div>
                  <div className="text-xs text-theme-muted">{r.status}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main>
          {full && full.status === "failed" && (
            <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-300 px-4 py-3 text-sm mb-4">
              Review status is <strong>failed</strong>. {full.error_message}
            </div>
          )}

          {full?.metrics && (
            <section className="glass rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-theme-secondary mb-3">
                Headline metrics
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-theme-muted uppercase tracking-wide">
                    <th className="text-left py-1.5 pr-3 font-medium">
                      Metric
                    </th>
                    <th className="text-right py-1.5 pr-3 font-medium">
                      This month
                    </th>
                    <th className="text-right py-1.5 pr-3 font-medium">
                      Last month
                    </th>
                    <th className="text-right py-1.5 font-medium">
                      Q1-2027 target
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {full.metrics.map((m) => (
                    <tr
                      key={m.key}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      <td className="py-1.5 pr-3 font-mono text-xs">{m.key}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {m.current ?? "—"}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-theme-muted">
                        {m.lastMonth ?? "—"}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-theme-secondary">
                        {m.target ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {full?.interventions && full.interventions.length > 0 && (
            <section className="glass rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-theme-secondary mb-3">
                Interventions delivered
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-theme-muted uppercase tracking-wide">
                    <th className="text-left py-1.5 pr-3 font-medium">Type</th>
                    <th className="text-right py-1.5 pr-3 font-medium">
                      Delivered
                    </th>
                    <th className="text-right py-1.5 pr-3 font-medium">
                      Active@30d
                    </th>
                    <th className="text-right py-1.5 font-medium">
                      Separated@30d
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {full.interventions.map((i) => (
                    <tr
                      key={i.intervention_type}
                      className="border-t border-theme-subtle"
                    >
                      <td className="py-1.5 pr-3 font-mono text-xs">
                        {i.intervention_type}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {i.count}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {i.outcome_30d_active ?? 0}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {i.outcome_30d_separated ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {full?.narrative_md && (
            <section className="glass rounded-xl p-5">
              <h3 className="text-sm font-semibold text-theme-secondary mb-3">
                Memo
              </h3>
              <pre className="whitespace-pre-wrap text-sm text-theme-primary leading-relaxed font-sans">
                {full.narrative_md}
              </pre>
              {full.model_used && (
                <p className="text-xs text-theme-muted mt-4 font-mono">
                  generated by {full.model_used}
                  {full.prompt_version ? ` · ${full.prompt_version}` : ""}
                  {full.tokens_in != null && full.tokens_out != null
                    ? ` · ${full.tokens_in}in/${full.tokens_out}out tokens`
                    : ""}
                </p>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
